import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { toast } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  CalendarDays,
  Check,
  ChevronDown,
  ChevronUp,
  Filter,
  LinkIcon,
  MapPin,
  MoreHorizontal,
  Plus,
  Search,
  Trash2,
  QrCode
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from '@/components/ui/badge';

interface Meeting {
  id: string;
  _id: string;
  title: string;
  date: string;
  meeting_type: string;
  location_lat: number | null;
  location_lng: number | null;
  max_radius: number | null;
  attendance_count: number | null;
  meeting_code: string;
  location: string;
  created_at: string;
}

const MeetingList: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<keyof Meeting>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
  const [meetingDetails, setMeetingDetails] = useState<any>(null);

  useEffect(() => {
    fetchMeetings();
  }, []);
  
  const fetchMeetings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      console.log('Token:', token);
      console.log('Group ID:', user?.group_id);
      
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/meetings?group_id=${user?.group_id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const error = await response.json();
        console.error('Error response:', error);
        throw new Error(error.message || 'Failed to fetch meetings');
      }

      const data = await response.json();
      console.log('Meetings data:', data);
      setMeetings(data);
    } catch (error) {
      console.error('Error fetching meetings:', error);
      toast.error('Failed to load meetings');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle sort changes
  const handleSort = (field: keyof Meeting) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  
  // Sort and filter meetings
  const filteredAndSortedMeetings = meetings
    .filter(meeting => 
      meeting.title.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      if (sortField === 'date') {
        return sortDirection === 'asc' 
          ? new Date(a.date).getTime() - new Date(b.date).getTime()
          : new Date(b.date).getTime() - new Date(a.date).getTime();
      }
      
      const aValue = a[sortField];
      const bValue = b[sortField];
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      if (aValue === null) return sortDirection === 'asc' ? -1 : 1;
      if (bValue === null) return sortDirection === 'asc' ? 1 : -1;
      
      // For numeric values
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      return 0;
    });
    
  // Get sort direction icon
  const getSortIcon = (field: keyof Meeting) => {
    if (field === sortField) {
      return sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />;
    }
    return null;
  };
  
  const getAttendanceLink = (meeting: Meeting) => {
    return `${window.location.origin}/app/attend/${meeting.meeting_code}`;
  };
  
  const handleCopyLink = (meeting: Meeting) => {
    const link = getAttendanceLink(meeting);
    navigator.clipboard.writeText(link)
      .then(() => toast.success('Attendance link copied to clipboard'))
      .catch(() => toast.error('Failed to copy link'));
  };
  
  const handleShareClick = async (meeting: Meeting) => {
    setSelectedMeeting(meeting);
    setShareDialogOpen(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/meetings/${meeting.meeting_code}`);
      if (!response.ok) {
        throw new Error('Failed to fetch meeting details');
      }
      const data = await response.json();
      setMeetingDetails(data);
    } catch (error) {
      console.error('Error fetching meeting details:', error);
      toast.error('Failed to load meeting details');
    }
  };
  
  const handleDeleteClick = (meeting: Meeting) => {
    setSelectedMeeting(meeting);
    setDeleteDialogOpen(true);
  };
  
  const handleDeleteMeeting = async (meetingId: string) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/meetings/${meetingId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to delete meeting');
      }

      toast.success('Meeting deleted successfully');
      setDeleteDialogOpen(false);
      fetchMeetings(); // Refresh the list
    } catch (error) {
      console.error('Error deleting meeting:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to delete meeting');
    }
  };
  
  // Get meeting status badge
  const getMeetingStatusBadge = (date: string) => {
    const now = new Date();
    const meetingDate = new Date(date);
    
    // Check if the meeting is today
    const isToday = meetingDate.toDateString() === now.toDateString();
    
    if (isToday) {
      return (
        <Badge variant="outline" className="bg-[#F5E6C3] text-[#D4AF37]">
          Today
        </Badge>
      );
    }
    
    if (meetingDate > now) {
      return (
        <Badge variant="outline" className="bg-yellow-100 text-yellow-800">
          Upcoming
        </Badge>
      );
    }
    
    return (
      <Badge variant="outline" className="bg-green-100 text-green-800">
        <Check className="mr-1 h-3 w-3" />
        Completed
      </Badge>
    );
  };
  
  // Navigate to create new meeting
  const goToCreateMeeting = () => {
    navigate('/admin/meetings/new');
  };
  
  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <motion.div 
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
          className="space-y-1"
        >
          <h1 className="text-2xl font-bold">CDS Meetings</h1>
          <p className="text-gray-500">Manage and track all your CDS meetings</p>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Button onClick={goToCreateMeeting}>
            <Plus className="mr-2 h-4 w-4" />
            New Meeting
          </Button>
        </motion.div>
      </div>
      
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="flex flex-col md:flex-row gap-4"
      >
        <div className="relative flex-grow">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
          <Input
            placeholder="Search meetings..."
            className="pl-9"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        <Button variant="outline" className="flex items-center gap-2">
          <Filter className="h-4 w-4" />
          Filter
        </Button>
      </motion.div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="rounded-lg border shadow-sm bg-white">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => handleSort('title')}
                >
                  <div className="flex items-center">
                    Title
                    {getSortIcon('title')}
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => handleSort('date')}
                >
                  <div className="flex items-center">
                    <CalendarDays className="h-4 w-4 mr-1" />
                    Date
                    {getSortIcon('date')}
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => handleSort('meeting_type')}
                >
                  <div className="flex items-center">
                    Meeting Type
                    {getSortIcon('meeting_type')}
                  </div>
                </TableHead>
                <TableHead className="hidden md:table-cell">
                  <div className="flex items-center">
                    <MapPin className="h-4 w-4 mr-1" />
                    Location
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => handleSort('attendance_count')}
                >
                  <div className="flex items-center">
                    <Check className="h-4 w-4 mr-1" />
                    Attendees
                    {getSortIcon('attendance_count')}
                  </div>
                </TableHead>
                <TableHead>
                  Status
                </TableHead>
                <TableHead>
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array(5).fill(0).map((_, i) => (
                  <TableRow key={`loading-${i}`}>
                    <TableCell colSpan={7}>
                      <div className="h-12 bg-gray-100 rounded animate-pulse"></div>
                    </TableCell>
                  </TableRow>
                ))
              ) : filteredAndSortedMeetings.length === 0 ? (
                <TableRow key="no-meetings">
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    No meetings found. Try a different search term or create a new meeting.
                  </TableCell>
                </TableRow>
              ) : (
                filteredAndSortedMeetings.map((meeting) => (
                  <TableRow key={meeting.id} className="hover:bg-gray-50">
                    <TableCell className="font-medium">
                      {meeting.title}
                    </TableCell>
                    <TableCell>
                      {new Date(meeting.date).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Badge key={`badge-${meeting.id}`} className="bg-primary/10 text-primary hover:bg-primary/20 border-primary/20">
                        {meeting.meeting_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {meeting.location_lat && meeting.location_lng ? (
                        <span key={`location-${meeting.id}`} className="text-xs text-gray-500">
                          {meeting.location_lat.toFixed(4)}, {meeting.location_lng.toFixed(4)}
                        </span>
                      ) : (
                        'Not set'
                      )}
                    </TableCell>
                    <TableCell className="font-medium">
                      {meeting.attendance_count || 0}
                    </TableCell>
                    <TableCell>
                      {getMeetingStatusBadge(meeting.date)}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                            <span className="sr-only">Open menu</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem key={`share-${meeting.id}`} onClick={() => handleShareClick(meeting)}>
                            <QrCode className="h-4 w-4 mr-2" />
                            View QR Code
                          </DropdownMenuItem>
                          <DropdownMenuItem key={`delete-${meeting.id}`} onClick={() => handleDeleteClick(meeting)}>
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </motion.div>
      
      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Meeting Details</DialogTitle>
            <DialogDescription>
              Share this meeting code with corps members to mark their attendance
            </DialogDescription>
          </DialogHeader>
          <div className="p-4 border rounded-md bg-muted mt-4">
            <div className="space-y-4">
              {meetingDetails && (
                <>
                  <div className="space-y-2">
                    <p className="font-medium">Title:</p>
                    <p className="text-sm">{meetingDetails.title}</p>
                  </div>
                  <div className="space-y-2">
                    <p className="font-medium">Date & Time:</p>
                    <p className="text-sm">
                      {new Date(meetingDetails.datetime).toLocaleString()}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <p className="font-medium">Meeting Type:</p>
                    <p className="text-sm">{meetingDetails.meeting_type}</p>
                  </div>
                  <div className="space-y-2">
                    <p className="font-medium">Location:</p>
                    <p className="text-sm">{meetingDetails.location}</p>
                    <p className="text-xs text-gray-500">
                      Radius: {meetingDetails.radius}m
                    </p>
                  </div>
                </>
              )}
              <div className="space-y-2">
                <p className="font-medium">Meeting Code:</p>
                <code className="bg-gray-100 p-2 rounded block text-center text-lg">
                  {selectedMeeting?.meeting_code}
                </code>
              </div>
            </div>
          </div>
          <DialogFooter className="flex flex-col sm:flex-row sm:justify-between sm:space-x-2">
            <Button 
              className="mt-3 sm:mt-0" 
              onClick={() => {
                navigator.clipboard.writeText(selectedMeeting?.meeting_code || '');
                toast.success('Meeting code copied to clipboard');
              }}
            >
              Copy Meeting Code
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this meeting and all associated attendance records.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => selectedMeeting && handleDeleteMeeting(selectedMeeting._id)} 
              className="bg-destructive text-destructive-foreground"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MeetingList;
